import { features } from "app/features";
import { _t } from "app/i18n/i18n";
import { arrayUniqueByKey, toArray } from "lib/utils";
import { addTranslations } from "pages/redesign/menuFilterBar/api/getRegions";
import { removeParam } from "api/search/filterUrlMaker";
import { setQueryParam, toggleQueryParams, toggleQueryParamsCountry, toggleQueryParamsRegion } from "../Suggestion";

export function getSearchOptions(lang, qs, pubLangPref, aggregationsWithCount, assetClass, sectorData) {
  const getQueryParam = (param) => toArray(qs[param]);
  const getQueryParams = (params) => params.reduce((agg, value) => (qs[value] ? agg.concat(qs[value]) : agg), []);

  const analysts = {};
  analysts.values = getQueryParams(["analysts.name.en", "analysts.name.ja", "Analyst"]);
  analysts.options = union(addCount(analysts.values), aggregationsWithCount[`analysts.name.${lang}`]);
  analysts.isVisible = analysts.options?.length;
  analysts.onChange = (ev) => toggleQueryParams(ev.target.value, qs, `analysts.name.${lang}`);

  const companies = {};
  companies.values = getQueryParams(companyFields);
  companies.options = union(addCount(companies.values), aggregationsWithCount[`companies.name.${lang}`]);
  companies.isVisible = ["Home", "Equity", "SP__Credit"].includes(assetClass) && companies.options.length;
  companies.onChange = (ev) => toggleQueryParams(ev.target.value, qs, `companies.name.${lang}`);

  const themes = {};
  themes.values = getQueryParams(["themes.en", "themes.ja", "Theme", "Theme-JA"]);
  themes.options = union(addCount(themes.values), aggregationsWithCount[`themes.${lang}`]);
  themes.isVisible = features.themesFilter && themes.options.length;
  themes.onChange = (ev) => toggleQueryParams(ev.target.value, qs, `themes.${lang}`);

  const tradeIdeas = {};
  tradeIdeas.values = getQueryParams(["tradeIdeas.en", "tradeIdeas.ja", "tradeIdeas","Trade Ideas","Trade Ideas-JA"]);
  tradeIdeas.options = union(addCount(tradeIdeas.values), aggregationsWithCount[`tradeIdeas.${lang}`]);
  tradeIdeas.isVisible = tradeIdeas.options.length;
  tradeIdeas.onChange = (ev) => toggleQueryParams(ev.target.value, qs, `tradeIdeas.${lang}`);

  const sectors = {};
  sectors.values = getQueryParam(`gicsSectors.${lang}`);
  sectors.options = aggregationsWithCount[`gicsSectors.${lang}`];
  sectors.isVisible = ["Home", "Equity", "SP__Credit"].includes(assetClass) && sectors.options.length;
  sectors.onChange = (ev) => setQueryParam(`gicsSectors.${lang}`, ev.target.value, qs);
  sectors.type = "radio";

  const industryGroups = {};
  industryGroups.values = getQueryParam(`gicsIndustryGroups.${lang}`);
  industryGroups.options = getSubSector(sectors.values, lang, sectorData); // aggregationsWithCount[`gicsIndustryGroups.${lang}`];
  industryGroups.isVisible =
    ["Home", "Equity", "SP__Credit"].includes(assetClass) &&
    getQueryParam(`gicsSectors.${lang}`).length &&
    industryGroups.options.length;
  industryGroups.onChange = (ev) => toggleQueryParams(ev.target.value, qs, `gicsIndustryGroups.${lang}`);

  const ratings = {};
  ratings.values = getQueryParam(`attributes.priceOrRatingChange.${lang}`);
  ratings.options = aggregationsWithCount[`attributes.priceOrRatingChange.${lang}`];
  ratings.isVisible = ["Home", "Equity"].includes(assetClass) && ratings.options.length;
  ratings.onChange = (ev) => toggleQueryParams(ev.target.value, qs, `attributes.priceOrRatingChange.${lang}`);

  const contentType = {};
  contentType.values = getQueryParam(`contentType.${lang}`);
  contentType.options = aggregationsWithCount[`contentType.${lang}`];
  contentType.isVisible = contentType.options.length;
  contentType.onChange = (ev) => toggleQueryParams(ev.target.value, qs, `contentType.${lang}`);

  const dates = {};
  dates.values = qs.Date ? getQueryParam("Date") : "All Time";
  dates.date = getQueryParam("Date");
  dates.dateTo = getQueryParam("DateTo");
  dates.options = getDateOptions();
  dates.onChange = (ev) => setQueryParam("Date", ev.target.value, clearToDate(qs));
  dates.type = "radio";

  const languages = {};
  const langPref = pubLangPref.toString() === "ja,en" ? "en,ja" : pubLangPref.toString();
  languages.values = qs.language ? [getQueryParam("language").toString()] : [langPref];
  languages.options = getLanguageOptions();
  languages.onChange = (ev) => setQueryParam("language", ev.target.value.split(","), qs);
  languages.type = "radio";

  const regions = {};
  regions.values = getQueryParams(["regions.en", "regions.ja", "regions", "Region", "Region-JA"]);
  regions.options = union(addCount(regions.values), aggregationsWithCount[`regions.${lang}`]);
  regions.isVisible = regions.options.length;
  regions.onChange = (ev) => toggleQueryParamsRegion(ev.target.value, qs, `regions.${lang}`, ev.target.checked);

  const countries = {};
  countries.values = getQueryParams(["countries.en", "countries.ja", "countries", "Country", "Country-JA"]);
  countries.options = union(addCount(countries.values), aggregationsWithCount[`countries.${lang}`]);
  countries.isVisible = countries.options.length;
  countries.onChange = (ev) => toggleQueryParamsCountry(ev.target.value, qs, `countries.${lang}`, ev.target.checked);

  const currencies = {};
  currencies.values = getQueryParams(["currencies.en", "currencies.ja", "currencies", "Currency", "Currency-JA"]);
  currencies.options = union(addCount(currencies.values), aggregationsWithCount[`currencies.${lang}`]);
  currencies.isVisible = currencies.options.length && !["Equity"].includes(assetClass);
  currencies.onChange = (ev) => toggleQueryParams(ev.target.value, qs, `currencies.${lang}`);

  const formData = {
    analysts,
    companies,
    themes,
    tradeIdeas,
    regions,
    countries,
    sectors,
    industryGroups,
    ratings,
    contentType,
    dates,
    languages,
    currencies,
  };
  return formData;
}

const union = (arr1 = [], arr2 = []) => arrayUniqueByKey([...arr1, ...arr2], "key");
const addCount = (keys) => keys.map((key) => ({ key }));

const getLanguageOptions = () => [
  { key: "en,ja", label: _t("All") },
  { key: "en", label: _t("en") },
  { key: "ja", label: _t("ja") },
];
const getSubSector = (sectorName, lang, data) => {
  if (data && lang && sectorName.length > 0) {
    return data[lang][sectorName[0]];
  }
  return [];
};
const getDateOptions = () => ["1 Week", "1 Month", "6 Months", "1 Year", "All Time"].map(addTranslations);
const clearToDate = () => removeParam("DateTo");

const companyFields = [
  "companies.name.en",
  "companies.name.ja",
  "companies.nameWithTicker.en",
  "companies.nameWithTicker.ja",
  "CompanyWithTicker",
  "CompanyWithTicker-JA",
];
